type FormatType = 'currency' | 'currency_eur' | 'number' | 'date_de';
type FormatOptions = {
    autoCompleteDateSegments?: boolean;
};

export default class ValueFormatter {
    static format(value: number | string, type: FormatType, options: FormatOptions = {}): string | null {
        if (!value && value !== 0) {
            return null;
        }
        switch (type) {
            case 'currency':
                return this.formatCurrency(value, false);
            case 'currency_eur':
                return this.formatCurrency(value, true);
            case 'number':
                return this.parseLocaleNumber(value).toString();
            case 'date_de':
                return this.formatGermanDate(value, options.autoCompleteDateSegments !== false);
            default:
                return value.toString();
        }
    }

    private static formatGermanDate(value: number | string, autoCompleteSegments: boolean): string {
        const stringValue = value.toString().trim();
        const sqlDateMatch = stringValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);

        if (sqlDateMatch !== null) {
            return `${sqlDateMatch[3]}.${sqlDateMatch[2]}.${sqlDateMatch[1]}`;
        }

        if (/\D/.test(stringValue)) {
            return this.formatGermanDateSegments(stringValue, autoCompleteSegments);
        }

        return this.formatGermanDateDigits(stringValue.slice(0, 8), autoCompleteSegments);
    }

    private static formatGermanDateSegments(value: string, autoCompleteSegments: boolean): string {
        const parts = value.replace(/\D+/g, '.').split('.');
        const rawDay = (parts[0] ?? '').slice(0, 2);
        const rawMonth = parts[1] ?? '';
        const year = parts.slice(2).join('').slice(0, 4);
        let day = rawDay;
        let month = rawMonth.slice(0, 2);

        if (parts.length > 1 && rawDay.length === 1) {
            day = `0${rawDay}`;
        }

        if (parts.length > 2 && rawMonth.length === 1) {
            month = `0${rawMonth}`;
        }

        if (day === '' && month === '' && year === '') {
            return '';
        }

        if (parts.length === 2 && rawMonth.length > 2) {
            return `${day}.${rawMonth.slice(0, 6)}`;
        }

        let formattedDate = day;
        if (parts.length > 1) {
            formattedDate += '.';
        }
        formattedDate += month;

        if (parts.length > 2 || (autoCompleteSegments && month.length === 2)) {
            formattedDate += '.';
        }
        formattedDate += year;

        return formattedDate;
    }

    private static formatGermanDateDigits(numericValue: string, autoCompleteSegments: boolean): string {
        if (numericValue.length <= 1) {
            return numericValue;
        }

        if (numericValue.length <= 2) {
            return autoCompleteSegments ? `${numericValue}.` : numericValue;
        }

        if (numericValue.length <= 3) {
            return `${numericValue.slice(0, 2)}.${numericValue.slice(2)}`;
        }

        if (numericValue.length <= 4) {
            const formattedDate = `${numericValue.slice(0, 2)}.${numericValue.slice(2)}`;
            return autoCompleteSegments ? `${formattedDate}.` : formattedDate;
        }

        return `${numericValue.slice(0, 2)}.${numericValue.slice(2, 4)}.${numericValue.slice(4)}`;
    }

    private static formatCurrency(num: number | string, needCurrencySymbol = true): string {
        let numberValue: number;
        // 1. Prüfen: Ist der Input bereits eine echte Zahl?
        if (typeof num === 'number') {
            numberValue = num;
        }
            // 2. Ist es ein String im US/Internationalen Format (z.B. "2856.99")?
            // Bedingung: Enthält Punkt, aber KEIN Komma.
            // WICHTIG: Wir schließen Strings aus, die wie deutsche Tausender-Trennzeichen aussehen (z.B. "4.000").
        // Die Regex `!/\.\d{3}$/` prüft: Endet der String NICHT auf einen Punkt gefolgt von genau 3 Ziffern?
        else if (
            typeof num === 'string' &&
            num.includes('.') &&
            !num.includes(',') &&
            !/\.\d{3}$/.test(num) // Schutz vor Verwechslung mit deutschem Tausender-Punkt
        ) {
            numberValue = parseFloat(num);
        }
            // 3. Deutsches Format parsen (z.B. "2.856,99" oder "4.000")
        // Hier wird "4.000" korrekt als 4000 interpretiert.
        else {
            numberValue = this.parseLocaleNumber(num, 'de-DE');
        }

        const istGanzeZahl = numberValue % 1 === 0;

        const options: Intl.NumberFormatOptions = {
            minimumFractionDigits: istGanzeZahl ? 0 : 2,
            maximumFractionDigits: 2,
        };

        if (needCurrencySymbol) {
            options.style = 'currency';
            options.currency = 'EUR';
        } else {
            options.style = 'decimal';
        }

        return new Intl.NumberFormat('de-DE', options).format(numberValue);
    }

    private static parseLocaleNumber(value: number | string, locale: string = 'de-DE'): number {
        const parts: Intl.NumberFormatPart[] =
            new Intl.NumberFormat(locale).formatToParts(1.1);

        const decimalSeparator: string =
            parts.find((p: Intl.NumberFormatPart) => p.type === 'decimal')?.value ?? '.';

        const normalized = value
            .toString()
            .replace(new RegExp(`[^0-9+\\-${decimalSeparator}]`, 'g'), '')
            .replace(decimalSeparator, '.');

        return Number.parseFloat(normalized);
    }
}
